import {Component, OnInit, Input, OnChanges, ViewChild, Inject} from '@angular/core';
import {ConfService} from '../conf.service';
import {LeftpanelComponent, LoaderWithProgressComponent} from '../leftpanel/leftpanel.component';
import {ActivatedRoute} from '@angular/router';
import {FlatTreeControl} from '@angular/cdk/tree';
import {MatTree, MatTreeFlatDataSource, MatTreeFlattener} from '@angular/material/tree';
import {MatDialog} from '@angular/material';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {CodeViewerComponent} from '../devex/api1/language-commands/ace-editor/code-viewer.component';

export interface TreeNode {
    name: string;
    id: string;
    level: 'transformation' | 'migration' | 'batch' | 'record';
    metrics?: {
        totalRecords: number;
        passed: number;
        failed: number;
        skipped: number;
        status?: string;
    };
    children?: TreeNode[];
    parent?: TreeNode;
}


@Component({
    selector: 'app-data-transformation-result',
    templateUrl: './data-transformation-result.component.html',
    styleUrls: ['./data-transformation-result.component.scss']
})
export class DataTransformationResultComponent implements OnInit {
    transformationPlanId: any;
    transformationPlanResult: any;
    transformationResult: any;
    selectedTransformation: any;
    migrationResult: any;
    selectedMigration: any;
    batchResult: any;
    selectedBatch: any;
    records: any = [];
    selectedLevel = 0;
    selectedNode: any = null;
    selectedNodeId: any = null;
    selectedBreadcrumb = '';
    fetchingRecords = false;
    componentLoading = false;


    chartData: number[] = [];
    chartLabels = ['Passed', 'Failed', 'Skipped'];
    chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    boxWidth: 12,
                    font: {
                        size: 12
                    }
                }
            }
        }
    };
    retryRecordLoading: any = false;
    retryRecordId: any;
    intervalId: any = null;

    @ViewChild(MatTree) tree: MatTree<TreeNode>;
    transformationTreeData: TreeNode[] = [];
    treeControl = new FlatTreeControl<any>(
        node => node.level,
        node => !!node.children && node.children.length > 0
    );

    treeFlattener = new MatTreeFlattener(
        (node: TreeNode, level: number) => ({...node, level}),
        node => node.level,
        node => !!node.children && node.children.length > 0,
        node => node.children
    );

    dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    hasChild = (_: number, node: TreeNode) => !!node.children && node.children.length > 0;


    constructor(public confPost: ConfService, public platform: LeftpanelComponent,
                private route: ActivatedRoute, public dialog: MatDialog) {
    }

    onNodeSelect(node: any) {
        this.selectedNode = node;
        this.selectedNodeId = node.id;
        // console.log(node);
        this.selectedBreadcrumb = this.getBreadcrumbPath(node);
        this.selectedLevel = node.level;
        // console.log('Breadcrumb:', this.selectedBreadcrumb);
        if (node.level === 2 && this.batchResult) {
            this.selectedBatch = this.batchResult.find((batch: any) => batch.batchId === node.id) || null;
            if (this.selectedBatch && this.selectedBatch.status !== 'completed') {
                this.getBatchRecords(this.selectedBatch.batchId);
            }
        }
        if (node.level === 1 && this.migrationResult) {
            this.selectedMigration = this.migrationResult.find((migration: any) => migration.migrationId === node.id) || null;
        }
        if (node.level === 0 && this.transformationResult) {
            this.selectedTransformation = this.transformationResult.find((transformation: any) =>
                transformation.transformationId === node.id) || null;
        }
    }


    expandToFirstBatch(node: TreeNode): TreeNode | null {
        if (!node || !node.children || node.children.length === 0) {
            return null;
        }

        for (const child of node.children) {
            if (child.level === 'batch') {
                this.treeControl.expand(node); // expand parent
                return child;
            }

            const result = this.expandToFirstBatch(child);
            if (result) {
                this.treeControl.expand(node); // expand all ancestors
                return result;
            }
        }

        return null;
    }


    buildTree(): TreeNode[] {
        return this.transformationResult.map((t: any, tIndex: any) => {
            const tNode: TreeNode = {
                id: t.transformationId,
                name: `Transformation - ${tIndex + 1}`,
                level: 'transformation',
                metrics: {
                    totalRecords: t.totalRecords,
                    passed: t.passed,
                    failed: t.failed,
                    skipped: t.skipped,
                },
                children: [],
            };

            const tMigrations = this.migrationResult.filter(m => m.transformationId === t.transformationId);

            tNode.children = tMigrations.map((m: any, mIndex: any) => {
                const mNode: TreeNode = {
                    id: m.migrationId,
                    name: `Migration - ${mIndex + 1}`,
                    level: 'migration',
                    metrics: {
                        totalRecords: m.totalRecords,
                        passed: m.passed,
                        failed: m.failed,
                        skipped: m.skipped,
                    },
                    parent: tNode,
                    children: [],
                };

                const mBatches = this.batchResult.filter(b => b.migrationId === m.migrationId);

                mNode.children = mBatches.map((b: any, bIndex: any) => {
                    const bNode: TreeNode = {
                        id: b.batchId,
                        name: `Batch - ${bIndex + 1}`,
                        level: 'batch',
                        metrics: {
                            totalRecords: b.totalRecords,
                            passed: b.passed,
                            failed: b.failed,
                            skipped: b.skipped,
                        },
                        parent: mNode,
                    };

                    return bNode;
                });

                return mNode;
            });

            return tNode;
        });
    }

    getBreadcrumbPath(node: TreeNode): string {
        const path = [];
        let current: TreeNode | undefined = node;

        while (current) {
            path.unshift(current.name);
            current = current.parent;
        }

        return path.join(' > ');
    }

    setTree() {
        let expandedIds: string[] = [];
        if (this.treeControl.dataNodes && this.treeControl.dataNodes.length) {
            expandedIds = this.treeControl.dataNodes
                .filter(function (node: any) {
                    return this.treeControl.isExpanded(node);
                }.bind(this))
                .map(function (node: any) {
                    return node.id;
                });
        }
        const treeData = this.buildTree();
        this.dataSource.data = treeData;
        // setTimeout(() => {
        //     const firstBatchNode = this.expandToFirstBatch(treeData[0]);
        //     console.log('first', firstBatchNode);
        //     if (firstBatchNode) {
        //         this.selectedNode = firstBatchNode;
        //         this.selectedBreadcrumb = this.getBreadcrumbPath(firstBatchNode);
        //     }
        // }, 1000);
        setTimeout(() => {
            if (this.treeControl.dataNodes && this.treeControl.dataNodes.length) {
                this.treeControl.dataNodes.forEach(node => {
                    if (expandedIds.indexOf(node.id) > -1) {
                        this.treeControl.expand(node);
                    }
                    if (this.selectedNodeId && node.id === this.selectedNodeId) {
                        this.selectedNode = node;
                    }
                });
            }
        });
    }


    ngOnInit() {
        this.transformationPlanId = this.route.snapshot.paramMap.get('transformationPlanId')!;
        // console.log('ID:', this.transformationPlanId);
        if (this.transformationPlanId) {
            this.getTransformationPlanDetails();
        }
    }

    refreshData(value) {
        if (value) {
            if (!this.intervalId) {
                this.intervalId = setInterval(() => {
                    this.getTransformationPlanDetails(false);
                }, 5000);
            }
        } else {
            if (this.intervalId) {
                clearInterval(this.intervalId);
                this.intervalId = null;
            }
        }
    }

    getTransformationPlanDetails(flag = true) {
        if (flag) {
            this.componentLoading = true;
        }
        this.confPost.getDataTransformationPlanDetails(this.transformationPlanId).subscribe(res => {
            // console.log('transformation plan ---> ', res);
            this.componentLoading = false;
            const temp: any = res;
            if (temp && temp.length > 0) {
                this.transformationPlanResult = temp[0];
                if (this.transformationPlanResult && this.transformationPlanResult.transformationPlanId) {
                    this.chartData = [this.transformationPlanResult.passed, this.transformationPlanResult.failed,
                        this.transformationPlanResult.skipped];
                    this.getTransformationDetails(this.transformationPlanResult.transformationPlanId, flag);
                }
            }
        }, error => {
            this.componentLoading = false;
            this.platform.openSnackBar('Error while fetching data transformation plan result.', 'Error', {
                duration: 15000,
                verticalPosition: 'top',
                horizontalPosition: 'center'
            });
        });
    }


    getTransformationDetails(id: any, flag = true) {
        this.confPost.getDataTransformationByPlanId(id).subscribe(res => {
            // console.log('transformations ---> ', res);
            const temp: any = res;
            if (temp) {
                this.transformationResult = temp;
                this.selectedTransformation = this.transformationResult[0];
                if (this.selectedTransformation && this.selectedTransformation.transformationId) {
                    this.getMigrationDetails(id, flag);
                }
            }
        });
    }

    getMigrationDetails(id: any, flag = true) {
        this.confPost.getDataMigrationByTransformationPlanId(id).subscribe(res => {
            // console.log('migrations ---> ', res);
            const temp: any = res;
            if (temp) {
                this.migrationResult = temp;
                this.selectedMigration = this.migrationResult[0];
                if (this.selectedMigration && this.selectedMigration.migrationId) {
                    this.getBatchDetails(id, flag);
                }
            }
        });
    }

    getBatchDetails(id: any, flag = true) {
        this.confPost.getDataBatchesByTransformationPlanId(id).subscribe(res => {
            // console.log('batches ---> ', res);
            const temp: any = res;
            if (temp) {
                this.batchResult = temp;
                this.selectedBatch = this.batchResult[0];
                this.setTree();
                if (this.selectedBatch && this.selectedBatch.batchId) {
                    this.getBatchRecords(this.selectedBatch.batchId, flag);
                }
            }
        });
    }

    getBatchRecords(id: any, flag = true) {
        if (flag) {
            this.fetchingRecords = true;
        }
        this.confPost.getDataRecordsByBatchId(id).subscribe(res => {
            // console.log('records ---> ', res);
            this.fetchingRecords = false;
            const temp: any = res;
            if (temp) {
                this.records = temp;
            }
        });
    }

    viewResponse(record: any, title: any) {
        const dialogRef = this.dialog.open(ViewResponseLogDialog, {
            width: '65vw',
            height: 'auto',
            data: record
        });
        dialogRef.afterOpened().subscribe(() => {
            // this.cdr.detectChanges();
        });


    }

    retryRecord(id: any) {
        if (id) {
            const payload: any = {
                errorId: id
            };
            this.retryRecordLoading = true;
            this.retryRecordId = id;
            // console.log(this.retryRecordId);
            this.confPost.retryDataTransformation(payload).subscribe(res => {
                const temp: any = res;
                this.retryRecordLoading = false;
                this.retryRecordId = undefined;
                if (temp && temp.message === 'success') {
                }
            }, err => {
                this.retryRecordLoading = false;
                this.retryRecordId = undefined;
            });
        }
    }

}


@Component({
    selector: 'view-response-log',
    templateUrl: 'view-response-log.html',
    styleUrls: ['view-response-log.css']
})
export class ViewResponseLogDialog {

    responseHeaders: any;
    response: any;
    responseType: any;
    userView = 'json';
    returnType = '';
    responseData: any;
    jsonViewer: any;
    responseCode: any;
    error = false;
    errorMessage: string;
    @ViewChild('codeViewer') codeViewer: CodeViewerComponent;

    constructor(@Inject(MAT_DIALOG_DATA) public data: any, public dialogRef: MatDialogRef<ViewResponseLogDialog>,
                public platform: LeftpanelComponent) {
        dialogRef.disableClose = true;
        if (!this.data.status || this.data.status === '') {
            this.data.status = 'success';
        }
        if (this.data && this.data.additionalDetails && this.data.additionalDetails.response) {
            this.response = this.data.additionalDetails.response;
            // console.log(this.response);
            if (this.response && this.response.headers) {
                this.responseHeaders = this.response.headers;
                // console.log(this.responseHeaders);
                if (this.responseHeaders && this.responseHeaders.length > 0) {
                    const contentType = this.responseHeaders.filter(element => element.name.toLowerCase() === 'content-type');
                    // console.log(contentType);
                    if (contentType && contentType.length > 0 && contentType[0]['name'].toLowerCase() === 'content-type') {
                        if (contentType[0]['value'].includes('json')) {
                            this.responseType = 'json';
                            this.userView = 'json';
                        } else if (contentType[0]['value'].includes('html')) {
                            this.responseType = 'html';
                            this.userView = 'html';
                        } else if (contentType[0]['value'].includes('text')) {
                            this.responseType = 'text';
                            this.userView = 'text';
                        } else if (contentType[0]['value'].includes('xml')) {
                            this.responseType = 'xml';
                            this.userView = 'xml';
                        }
                        // console.log(this.responseType);
                        // console.log(this.userView);
                    }
                }
            }
            if (this.response && this.response.body) {
                if (this.response.body.startsWith('[{')) {
                    this.returnType = 'Array';
                    this.responseData = JSON.parse(this.response.body);
                    this.jsonViewer = this.stringifyjson(this.responseData);
                } else if (this.response.body.startsWith('{')) {
                    this.returnType = 'JSON Object';
                    this.responseData = [];
                    this.responseData.push(JSON.parse(this.response.body));
                    this.jsonViewer = this.stringifyjson(this.responseData[0]);
                } else if (this.response.body.startsWith('[')) {
                    this.returnType = 'Array';
                    this.responseData = JSON.parse(this.response.body);
                    this.jsonViewer = this.stringifyjson(this.responseData);
                }
            }
            if (this.response && this.response.statusCode) {
                this.responseCode = this.response.statusCode;
            }
            if (this.responseCode && this.responseCode >= 400) {
                this.error = true;
            } else {
                this.error = false;
            }

            if (this.error) {
                if (this.response && this.response.body && this.response.body['message']) {
                    this.responseData = JSON.parse(JSON.stringify(this.response.body));
                    this.errorMessage = this.responseData['message'];
                    // this.responseError = this.responseData['error'];
                } else if (!this.response.body['message'] && this.response && this.response.body) {
                    this.responseData = JSON.parse(JSON.stringify(this.response.body));
                    if (this.responseData.hasOwnProperty('error')) {
                        this.errorMessage = this.responseData['error']['message'];
                    } else {
                        this.errorMessage = this.response.body;
                    }
                }
            }
        }
    }

    getStatusCodeStyle(val) {
        if (val < 400) {
            return 'status-ok';
        } else {
            return 'status-not-ok';
        }
    }

    copyToClipboard() {
        const ele_textAsResponse = document.querySelector('#textAsResponse');
        if (this.codeViewer) {
            const textToCopy = this.codeViewer.getContent();
            navigator.clipboard.writeText(textToCopy);
        } else if (ele_textAsResponse !== null) {
            const textToCopy = ele_textAsResponse.textContent;
            navigator.clipboard.writeText(textToCopy);
        }
        this.platform.openSnackBar('Copied to clipboard.', 'Success', {
            duration: 2000,
            verticalPosition: 'top',
            horizontalPosition: 'center'
        });
    }

    stringifyjson(val) {
        if (val != null) {
            return JSON.stringify(val, null, 2).trim();
        } else {
            return val;
        }
    }

    save() {
        this.dialogRef.close();
    }

    close() {
        this.dialogRef.close();
    }
}
